/* eslint-disable no-case-declarations */
import { Project } from "@/api/query/useProjectQuery";
import UserAvatar from "@/components/ui/userAvatar";
import { Gantt, ViewMode } from "@/components/views/custom/gantt/src";
import { useUser } from "@/hooks/useUser";
import { OrgListOfNonWorkingDaysEnum } from "@backend/src/schemas/organisationSchema";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import Loader from "@/components/common/Loader";
import useOrganisationDetailsQuery from "@/api/query/useOrganisationDetailsQuery";
// import ProjectFilter from "./workloadPlannigFilter";
// import { FIELDS } from "@/api/types/enums";
import NoFilterProject from "./noProject";
import { Button } from "@/components/ui/button";
import Collaps from "@/assets/svg/collaps.svg";
import Expand from "@/assets/svg/expand.svg";
import ZoomOut from "@/assets/svg/ZoomOut.svg";
import ZoomIn from "@/assets/svg/ZoomIn.svg";
import FullScreen from "@/assets/svg/FullScreen.svg";
import ExitFullscreen from "@/assets/svg/ExitFullscreen.svg";
import Refresh from "@/assets/svg/Refresh.svg";
import { useTranslation } from "react-i18next";
import TaskSubTaskForm from "@/components/tasks/taskSubTaskForm";
import { useGetCapacityPlanningMutation } from "@/api/mutation/useGetCapacityPlanningMutation";
import { useNavigate } from "react-router-dom";
import { darkenColor } from "@/components/views/custom/gantt/src/helpers/colorMixer";
import useDebounce from "@/hooks/useDebounce";
import useProjectWithoutCalulationQuery from "@/api/query/useProjectWithoutCalulationQuery";
import RedFlag from "@/assets/svg/RedFlag.svg";
import OrangeFlag from "@/assets/svg/OrangeFlag.svg";
import GreenFlag from "@/assets/svg/GreenFlag.svg";
import { calculateTaskEffort } from "@/hooks/useEffortCalculation";

interface columnsType {
  field: string;
  name: string;
  sortable?: boolean;
  width: number;
  headerRender?: () => React.ReactNode;
  render: (data: any) => React.ReactNode;
}

export interface ProjectNode extends Project {
  type: "project";
  progress: number;
}

export interface ProgramNode {
  type: "program";
  programId: string;
  programName: string;
  progress: number;
  projects: ProjectNode[];
}

export interface PortfolioNode {
  type: "portfolio";
  portfolioId: string;
  portfolioName: string;
  progress: number;
  projects: ProjectNode[];
  programs: ProgramNode[];
}

export type HierarchyItem = PortfolioNode | ProgramNode | ProjectNode;

function WorkloadPlaning() {
  const [projects, setProjects] = useDebounce<any>([], 600);
  const allProjects = useProjectWithoutCalulationQuery(false);
  const { user } = useUser();
  // const [selectedDateType, setSelectedDateType] = useState<
  //   "actual" | "estimated"
  // >("estimated");
  const [filterUnit, setFilterUnit] = useState(0);
  const [expanded, setExpanded] = useState<string[]>([]);
  const { t } = useTranslation();
  const [zoomState, setZoomState] = useState(filterUnit);
  const [view, setView] = useState(ViewMode.Day);
  const [expandAll, setExpandAll] = useState(false);
  const Divref = useRef<HTMLDivElement | null>(null);
  const GanttHeight = useRef<number>(600);
  const [ganttDate, setGanttDate] = useState({
    start: new Date(),
    end: new Date(),
  });
  const [OrganisationMemeber, setOrganisationMemeber] = useState<any[]>([]);
  const organisationDetailsQuery = useOrganisationDetailsQuery(
    localStorage.getItem("organisation-id") ?? ""
  );

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedTask, setSelectedTask] = useState({
    taskId: "",
    projectId: "",
  });
  // const navigate = useNavigate();

  // *** OPTIMIZATION 1: Memoize static data ***
  const viewModes = useMemo(
    () => [
      ViewMode.Day,
      ViewMode.Week,
      ViewMode.Month,
      ViewMode.QuarterYear,
      ViewMode.Year,
    ],
    []
  );

  const minZoom = 0;
  const maxZoom = viewModes.length - 1;

  // *** OPTIMIZATION 2: Memoize non-working days calculation ***
  const nonWorkingDays = useMemo(() => {
    const nWd =
      user?.userOrganisation?.map((org) => org.organisation.nonWorkingDays) ||
      [];
    const mapDayToNumber = (day: OrgListOfNonWorkingDaysEnum) => {
      switch (day) {
        case "SUN":
          return 0;
        case "MON":
          return 1;
        case "TUE":
          return 2;
        case "WED":
          return 3;
        case "THU":
          return 4;
        case "FRI":
          return 5;
        case "SAT":
          return 6;
        default:
          return 0;
      }
    };
    const result: number[] = [];
    nWd.forEach((days) => {
      days.forEach((day) => {
        result.push(mapDayToNumber(day));
      });
    });
    return result;
  }, [user?.userOrganisation]);

  const holidayDates = useMemo(() => {
    return (
      user?.userOrganisation[0]?.organisation?.orgHolidays?.map(
        (h) => new Date(h.holidayStartDate)
      ) ?? []
    );
  }, [user?.userOrganisation]);

  const UTCDateConverter = useCallback((date: Date) => {
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
    );
  }, []);
  const AssignedCell = React.memo(function AssignedCell({
    data,
  }: {
    data: any;
  }) {
    const assignRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(4); // default to 4

    useEffect(() => {
      if (!assignRef.current) return;

      const element = assignRef.current;

      const calculateVisibleCount = () => {
        const avatarWidth = 32;
        const spacing = 4;
        const containerWidth = element.clientWidth || 0;
        const maxCount = Math.floor(
          containerWidth / (avatarWidth - 8 + spacing)
        );
        setVisibleCount(maxCount > 0 ? maxCount - 2 : 0);
      };

      const resizeObserver = new ResizeObserver(() => {
        calculateVisibleCount();
      });

      resizeObserver.observe(element);

      // Initial call
      calculateVisibleCount();

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    const visibleAvatars = useMemo(() => {
      const assignList = data.assign ?? [];
      const visible = assignList.slice(0, visibleCount > 0 ? visibleCount : 1);
      const hiddenCount = Math.max(assignList.length - visible.length, 0);

      return (
        <>
          {visible.map((item: any, index: number) => {
            const efforts = calculateTaskEffort(
              item?.workingHours ?? 8,
              data.duration,
              8
            );

            return (
              <TooltipProvider delayDuration={0}>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger>
                    <div className={`relative group ${index > 0 ? "" : ""}`}>
                      <UserAvatar
                        className="shadow-sm shadow-gray-300 border-2 border-white w-7 h-7"
                        user={item.user}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs flex flex-col gap-1 max-w-[220px]">
                      <div className="font-semibold text-gray-700 w-full">
                        {item.user?.firstName && item.user?.lastName
                          ? `${item.user.firstName} ${item.user.lastName}`
                          : item.user?.email}
                      </div>
                      {data.type == "task" && (
                        <div className="flex justify-between">
                          <span className="text-gray-500 text-nowrap">
                            {t("Task Effort (%)")}:
                          </span>
                          <span>{efforts}%</span>
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}

          {hiddenCount > 0 && (
            <TooltipProvider delayDuration={0}>
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <div
                    className="ml-1 w-7 h-7 aspect-square justify-center items-center flex  rounded-full bg-slate-200 outline-none"
                    data-userid={"all"}
                    data-showallassing="true"
                  >
                    +{hiddenCount}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-xs flex flex-col gap-1 max-w-[220px]">
                    <div className="font-semibold text-gray-700 flex justify-between">
                      <div className="w-full"></div>
                      {data.type == "task" && (
                        <div className="text-nowrap">
                          {t("Task Effort (%)")}
                        </div>
                      )}
                    </div>
                    {(data.assign.slice(visibleCount) ?? []).map(
                      (hiddenItem: any) => {
                        const effort = calculateTaskEffort(
                          hiddenItem?.workingHours ?? 8,
                          data.duration,
                          8
                        );
                        const userLabel =
                          hiddenItem.user?.firstName &&
                          hiddenItem.user?.lastName
                            ? `${hiddenItem.user.firstName} ${hiddenItem.user.lastName}`
                            : hiddenItem.user?.email;

                        return (
                          <div className="flex justify-between items-center">
                            <span className="truncate mr-2">{userLabel}</span>
                            {data.type == "task" && (
                              <div className="flex justify-between">
                                <span>{effort}%</span>
                              </div>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </>
      );
    }, [data.assign, data.duration, visibleCount]);

    return (
      <div className="ml-3 flex w-full !overflow-hidden" ref={assignRef}>
        {visibleAvatars}
      </div>
    );
  });
  const cols: columnsType[] = [
    {
      field: "name",
      name: t("project_name"),
      sortable: true,
      width: 300,
      render(data: any) {
        if (data.flag) {
          return (
            <div className="flex gap-2 items-center ml-1 w-full !text-lg">
              {data.optional.flag == "Green" ? (
                <img src={GreenFlag} className="h-3 w-3" />
              ) : data.optional.flag == "Red" ? (
                <img src={RedFlag} className="h-3 w-3" />
              ) : data.optional.flag == "Orange" ? (
                <img src={OrangeFlag} className="h-3 w-3" />
              ) : (
                ""
              )}
              <div
                className={`!truncate !text-base w-full ${
                  data.type == "project" ? "!font-semibold" : ""
                }`}
              >
                {data.name}
              </div>
            </div>
          );
        } else {
          return (
            <div
              key={data.flag}
              className={`!truncate !text-base flex items-center gap-1 w-full`}
            >
              <div
                className={`!truncate !text-base w-fit ${
                  data.type == "project" ? "!font-semibold" : ""
                }`}
              >
                {data.name}
              </div>
            </div>
          );
        }
      },
    },
    {
      field: "assign",
      name: t("Assignees"),
      width: 200,
      render(data: any) {
        return <AssignedCell data={data} />;
      },
    },
  ];
  const [columns, setColumns] = useState<any[]>(cols);

  const zoominout = useCallback(
    (num: number) => {
      const newZoomState = Math.max(minZoom, Math.min(num, maxZoom));
      setZoomState(newZoomState);
      setView(viewModes[newZoomState]);
    },
    [minZoom, maxZoom, viewModes]
  );

  const expandToggle = useCallback((id: string) => {
    setExpanded((prev) => {
      if (prev?.includes(id)) {
        return prev.filter((t) => t !== id);
      } else {
        return [...(prev ?? []), id];
      }
    });
  }, []);

  const setGanttHeight = useCallback((num: number) => {
    GanttHeight.current = num;
  }, []);

  const updateHeight = useCallback(() => {
    setGanttHeight(
      Divref.current?.offsetHeight ? Divref.current.offsetHeight - 70 : 600
    );
  }, [setGanttHeight]);

  const enterFullscreen = useCallback(() => {
    const elem = document.documentElement;
    if (!isFullscreen) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }, [isFullscreen]);

  // *** OPTIMIZATION 10: Batch state updates where possible ***
  const LIMIT = 8;

  const [page, setPage] = useState(0); // current page index
  const [_, setHasMore] = useState(true);
  const loadingMore = useRef(false);
  const [resource, setResource] = useState<any>();
  useEffect(() => {
    fetchData(0);
    allProjects.refetch();
  }, []);

  const { mutate,isPending } = useGetCapacityPlanningMutation();
  const [inPercentage, setInPercentage] = useState(true);
  const [featchStatus, setFeatchStatus] = useState("");
  const fetchData = useCallback(
    (pageNumber = 0) => {
      if (pageNumber === 0) {
        setResource(null);
        setHasMore(true);
      }

      const payload = {
        viewMode: view,
        inPercentage: inPercentage,
        isWorkloadPlanning: true,
        dates: [], // or pass actual dates if needed
        skip: pageNumber * LIMIT,
        limit: LIMIT,
      };
      mutate(payload as any, {
        onSuccess: (res: any) => {
          setFeatchStatus("Featched");
          // ---------------------------
          // 1) SET PROJECTS
          // ---------------------------

          const newProjects = res.data.data.mapedProject.map((t: any) => {
            const start = new Date(t.start);
            start.setUTCHours(0, 0, 0, 0);
            start.setHours(0, 0, 0, 0);

            const end = new Date(t.end);
            if (!t.project) {
            } else {
              //  end.setDate(end.getDate() - 1);
            }
            end.setUTCHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 0);

            let progress = Number(t.progress).toFixed(0);
            return { ...t, start, end, progress };
          });

          setProjects((prev: any[]) =>
            pageNumber === 0 ? newProjects : [...prev, ...newProjects]
          );

          if (pageNumber === 0) {
            setResource(res.data.data.resource);
          }

          if (newProjects.length < LIMIT) {
            setHasMore(false);
          }

          loadingMore.current = false;
        },

        onError: () => {
          loadingMore.current = false;
        },
      });
    },
    [view, inPercentage]
  );
  useEffect(() => {
    fetchData(page);
  }, [inPercentage, view]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);

      const navbar = document.getElementById("navbar");
      const sidebar = document.getElementById("Sidebar");

      if (document.fullscreenElement) {
        navbar?.classList.add("!hidden");
        sidebar?.classList.add("!hidden");
      } else {
        navbar?.classList.remove("!hidden");
        sidebar?.classList.remove("!hidden");
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    if (document.fullscreenElement) {
      handleFullscreenChange();
    }
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const tempV: string[] = [];
    if (expandAll) {
      projects.forEach((task: any) => {
        if (task.type === "project") {
          tempV.push(task.id);
        }
      });
    }
    const hasChanged =
      expanded?.length !== tempV.length ||
      !expanded?.every((id, index) => id === tempV[index]);
    if (hasChanged) {
      setExpanded(tempV);
    }
  }, [expandAll]);

  useEffect(() => {
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (Divref.current) {
      resizeObserver.observe(Divref.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [updateHeight]);

  useEffect(() => {
    zoominout(filterUnit);
  }, [filterUnit, zoominout]);

  useEffect(() => {
    if (projects.length > 0) {
      const sortedProjects = [...projects];
      setGanttDate({
        start: UTCDateConverter(new Date(sortedProjects[0].start)),
        end: UTCDateConverter(
          new Date(sortedProjects[sortedProjects.length - 1].end)
        ),
      });
    }
  }, [projects]);

  // *** OPTIMIZATION 11: Throttle scroll events ***
  useEffect(() => {
    let timeoutId: number;
    const handleScroll = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault();
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (event.deltaY < 0 && zoomState > minZoom) {
            zoominout(zoomState - 1);
          } else if (event.deltaY > 0 && zoomState < maxZoom) {
            zoominout(zoomState + 1);
          }
        }, 100);
      }
    };

    const div = Divref.current;
    if (div) {
      div.addEventListener("wheel", handleScroll, { passive: false });
    }

    return () => {
      if (div) {
        div.removeEventListener("wheel", handleScroll);
      }
      clearTimeout(timeoutId);
    };
  }, [zoomState, minZoom, maxZoom, zoominout]);

  // useEffect(() => {
  //   setColumns(memoizedColumns);
  // }, [memoizedColumns]);

  // const visibleCols = useMemo(() => {
  //   const filteredCols = memoizedColumns
  //   if (columns && columns.length > 0) {
  //     const sortedCol = columns
  //       .map((element: any) =>
  //         filteredCols.find((t) => t.field == element.field)
  //           ? {
  //             ...filteredCols.find((t) => t.field == element.field)!,
  //             width: element.width,
  //           }
  //           : null
  //       )
  //       .filter(Boolean); // remove nulls in case columns has old fields
  //     return sortedCol.length > 0 ? sortedCol : filteredCols;
  //   }
  //   return filteredCols;
  // }, [memoizedColumns, columns]);

  useEffect(() => {
    setOrganisationMemeber(
      organisationDetailsQuery.data?.data.data.userOrganisation ?? []
    );
  }, [organisationDetailsQuery.data?.data.data]);

  const navigate = useNavigate();
  const handleDoubleClick = useCallback(
    (e: any) => {
      if ((e as any).flag == "task") {
        const project = projects.find(
          (project: any) => e.level.split(".")[0] === project?.level
        );
        setSelectedTask({
          taskId: e.id,
          projectId: project.id ?? "",
        });
      } else {
        navigate("/project-details/" + e.id);
      }
    },
    [projects, navigate]
  );
  const [filterData, setfilterData] = useState<any>([]);
  useEffect(() => {
    setfilterData(
      projects.map((item: any) => {
        const isExpanded = expanded.includes(item.id);
        return {
          ...item,
          hideChildren: !isExpanded,
          subtask: !isExpanded ? [] : item.subtask ?? [],
          styles: {
            backgroundSelectedColor:
              item.type === "project"
                ? darkenColor("#3b82f6", 0.2)
                : item.type === "milestone"
                ? darkenColor("#fa7181", 0.2)
                : darkenColor("#22c55e", 0.2),
            progressSelectedColor:
              item.type === "project"
                ? darkenColor("#3b82f6", 0.4)
                : item.type === "milestone"
                ? darkenColor("#fa7181", 0.4)
                : darkenColor("#22c55e", 0.4),
            backgroundColor:
              item.type === "project"
                ? darkenColor("#3b82f6", 0.1)
                : item.type === "milestone"
                ? darkenColor("#fa7181", 0.1)
                : darkenColor("#22c55e", 0.1),
            progressColor:
              item.type === "project"
                ? darkenColor("#3b82f6", 0.25)
                : item.type === "milestone"
                ? darkenColor("#fa7181", 0.25)
                : darkenColor("#22c55e", 0.25),
          },
        };
      })
    );
  }, [projects]);

  useEffect(() => {
    if (!filterData) return;

    const updated = filterData.map((item: any) => {
      const isExpanded = expanded.includes(item.id);
      return {
        ...item,
        hideChildren: !isExpanded,
        subtask: !isExpanded ? [] : item.subtask ?? [],
      };
    });

    setfilterData(updated);
  }, [expanded, expandAll, columns]);
  // const [filterApplied, setfilterApplied] = useState(false)
  // const projectFilter = useMemo(
  //   () => (
  //     <ProjectFilter
  //       project={projects}
  //       fieldToShow={[FIELDS.STATUS, FIELDS.DELAY, FIELDS.DATE]}
  //       filteredData={(project) => {
  //         setfilterData(
  //           project?.map((item: any) => {
  //             const isExpanded = expanded.includes(item.id);
  //             return {
  //               ...item,
  //               hideChildren: !isExpanded,
  //               subtask: !isExpanded ? [] : item.subtask ?? [],
  //               styles: {
  //                 backgroundSelectedColor:
  //                   item.type === "project"
  //                     ? darkenColor("#3b82f6", 0.2)
  //                     : item.type === "milestone"
  //                     ? darkenColor("#fa7181", 0.2)
  //                     : darkenColor("#22c55e", 0.2),
  //                 progressSelectedColor:
  //                   item.type === "project"
  //                     ? darkenColor("#3b82f6", 0.4)
  //                     : item.type === "milestone"
  //                     ? darkenColor("#fa7181", 0.4)
  //                     : darkenColor("#22c55e", 0.4),
  //                 backgroundColor:
  //                   item.type === "project"
  //                     ? darkenColor("#3b82f6", 0.1)
  //                     : item.type === "milestone"
  //                     ? darkenColor("#fa7181", 0.1)
  //                     : darkenColor("#22c55e", 0.1),
  //                 progressColor:
  //                   item.type === "project"
  //                     ? darkenColor("#3b82f6", 0.25)
  //                     : item.type === "milestone"
  //                     ? darkenColor("#fa7181", 0.25)
  //                     : darkenColor("#22c55e", 0.25),
  //               },
  //             };
  //           }) ?? []
  //         );
  //       }}
  //       appleed={(applied) => {
  //         setfilterApplied(applied);
  //       }}
  //     />
  //   ),
  //   [projects]
  // );
  const tasksRef = useRef([]);
tasksRef.current = filterData;
  const renderGantt = useMemo(() => {
    if (filterData && filterData?.length > 0 ) {
      return (
        <Gantt
          resourceMember={OrganisationMemeber}
          canCreateDependency={false}
          startEndIndicater={false}
          ganttHeight={GanttHeight.current}
          onExpanderClick={(e) => expandToggle(e.id)}
          columns={(columns as columnsType[]) ?? ([] as columnsType[])}
          projectStartDate={ganttDate.start}
          projectEndDate={ganttDate.end}
          preStepsCount={2}
          showWorkload={true}
          holidays={holidayDates}
          setColumns={(incoming) => {
            setColumns(incoming);
          }}
          viewMode={view}
          nonWorkingDays={nonWorkingDays}
          showContextManu={false}
          onContextManuEvent={() => {}}
          onScroll={(e) => {
            const div = e.currentTarget;
            const bottomReached =
              div.scrollHeight - div.scrollTop - div.clientHeight < 50;
            if (bottomReached && !loadingMore.current) {
              const nextPage = page + 1;
              setPage(nextPage);
              fetchData(nextPage);
            }
          }}
          onDepandanyClick={() => {}}
          onColorChange={() => {}}
          onDepandancyDraging={() => {}}
          onDepandancyDragEnd={() => {}}
          tasks={tasksRef.current}
          onDoubleClick={handleDoubleClick}
          apiResourceData={resource}
          newTask={undefined}
          setAPIData={(data) => {
            setInPercentage(data.inPercentage);
          }}
          workloadLoader={isPending}
          inPercentage={inPercentage}
          setSelected={(e) => {
            let currentProject = e.project;
            const projectIds: string[] = [];

            while (currentProject) {
              const parent = filterData.find(
                (task: any) => task.id === currentProject
              );
              if (!parent) break;
              projectIds.push(currentProject);
              currentProject = parent.parentId;
            }

            setExpanded((prev) => [...(prev ?? []), ...projectIds]);
          }}
        />
      );
    } else if (projects && projects.length && filterData?.length === 0) {
      return (
        <div className="w-full h-full bg-white">
          <NoFilterProject />
        </div>
      );
    } else {
      return (
        !featchStatus && (
          <Loader className="absolute top-0 left-0 bg-white/60 z-20" />
        )
      );
    }
  }, [filterData, view, columns,isPending]);

  return (
    <div className="flex flex-col gap-2 grow p-4 h-full">
      <div className="flex justify-start items-center gap-2">
        {/* {projectFilter} */}
        <div className="flex gap-2 items-center justify-start mr-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <li className="list-none">
                  <Button
                    variant={"none"}
                    onClick={() => setExpandAll(!expandAll)}
                    size={"sm"}
                  >
                    <img
                      src={!expandAll ? Collaps : Expand}
                      alt=""
                      className="w-5 min-w-5"
                    />
                  </Button>
                </li>
              </TooltipTrigger>
              <TooltipContent className="bg-white rounded-md px-2 py-1.5 shadow-lg border border-gray-450/30">
                <p className="text-sm">
                  {t(!expandAll ? "expand" : "collapse")}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <li className="list-none">
                  <Button
                    variant={"none"}
                    className="select-none"
                    disabled={filterUnit == 0}
                    onClick={() => setFilterUnit((prev) => prev - 1)}
                  >
                    <img src={ZoomIn} className="w-4 min-w-4" />
                  </Button>
                </li>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm">{t("zoomin")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <li className="list-none">
                  <Button
                    variant={"none"}
                    className="select-none"
                    disabled={filterUnit == 4}
                    onClick={() => setFilterUnit((prev) => prev + 1)}
                  >
                    <img src={ZoomOut} className="w-4 min-w-4" />
                  </Button>
                </li>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm">{t("zoomout")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <li className="list-none">
                  <Button
                    variant={"none"}
                    className="select-none"
                    onClick={enterFullscreen}
                  >
                    <img
                      src={!isFullscreen ? FullScreen : ExitFullscreen}
                      className={`${
                        !isFullscreen ? "w-5 min-w-5" : "w-3.5 min-w-3.5"
                      }`}
                    />
                  </Button>
                </li>
              </TooltipTrigger>
              <TooltipContent className="bg-white rounded-md px-2 py-1.5 shadow-lg border border-gray-450/30">
                <p className="text-sm">
                  {t(!isFullscreen ? "full_Screen" : "full_exit_screen")}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <li className="list-none">
                  <Button
                    variant={"none"}
                    className="select-none"
                    onClick={() => fetchData(0)}
                  >
                    <img src={Refresh} className={`w-3.5 min-w-3.5`} />
                  </Button>
                </li>
              </TooltipTrigger>
              <TooltipContent className="bg-white rounded-md px-2 py-1.5 shadow-lg border border-gray-450/30">
                <p className="text-sm">{t("reload")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <div className="h-[83%]" ref={Divref}>
        {renderGantt}
      </div>

      {(Boolean(selectedTask.projectId) || Boolean(selectedTask.taskId)) && (
        <TaskSubTaskForm
          taskId={selectedTask.taskId}
          projectId={selectedTask.projectId}
          close={() => {
            setSelectedTask({ taskId: "", projectId: "" });
            fetchData();
          }}
        />
      )}
    </div>
  );
}

export default WorkloadPlaning;
